import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { useMatch, useParams }      from 'react-router-dom';
import { decode }                   from '../../../../../helpers/utils';
import { setAllPaths, clearAllNavPaths, clearNavSelection, setSaveStatus } from '../../../../../store/slices/navigationSlice';
import { saveNavigationPaths, loadNavPathsFromProject } from './navigationService';
import { useProjectHeader } from '../../../../../pages/buildProject/Helpers/pageDiv/ProjectHeaderContext';
import {
  pathsToLinesGeoJSON,
  nodesToGeoJSON,
  previewToGeoJSON,
  eraserSegmentsToGeoJSON,
  emptyCol,
} from './navigationUtils';

export const NAV_SOURCES = {
  lines:      'nav-lines',
  nodes:      'nav-nodes',
  preview:    'nav-preview',
  highlight:  'nav-highlight',
  eraserSegs: 'nav-eraser-segs',
};

export const NAV_LAYERS = {
  mainLine:       'nav-main-line',
  subLine:        'nav-sub-line',
  selectedLine:   'nav-selected-line',
  hitLine:        'nav-hit-line',
  nodes:          'nav-nodes-circle',
  nodesPin:       'nav-nodes-pin',
  nodesSnap:      'nav-nodes-snap',
  previewLine:    'nav-preview-line',
  previewDot:     'nav-preview-dot',
  highlightLine:  'nav-highlight-line',
  highlightDash:  'nav-highlight-dash',
  highlightArrow: 'nav-highlight-arrow',
  eraserSegs:     'nav-eraser-segs-line',
};

const PATH_ONLY_LAYERS = [
  'nav-main-line', 'nav-sub-line', 'nav-selected-line', 'nav-hit-line',
  'nav-nodes-circle', 'nav-nodes-pin', 'nav-nodes-snap',
  'nav-preview-line', 'nav-preview-dot', 'nav-eraser-segs-line',
];

export const navSourceRef = {
  setLines:             null,
  setNodes:             null,
  setPreview:           null,
  setHighlight:         null,
  setEraserSegs:        null,
  setPathLayersVis:     null,
  _latestHighlight:     null,
  _desiredVisibility:   'none',
  _shortestPathActive:  false,
  applyVisibility:      null,
  onInit:               null,  
};

export default function NavigationLayer() {
  const map         = useSelector((s) => s.map.mapContainer);
  const projectData = useSelector((state) => state.api.projectData);
  const initialised = useRef(false); 
  
  useEffect(() => {
    if (!map) return;

    let dashRafId = null; 
    let cancelled = false;

    const teardown = () => {
      if (dashRafId) { cancelAnimationFrame(dashRafId); dashRafId = null; }
      Object.values(NAV_LAYERS).forEach((l) => {
        try { if (map.getLayer(l)) map.removeLayer(l); } catch {}
      });
      Object.values(NAV_SOURCES).forEach((s) => {
        try { if (map.getSource(s)) map.removeSource(s); } catch {}
      });
      navSourceRef.setLines         = null;
      navSourceRef.setNodes         = null;
      navSourceRef.setPreview       = null;
      navSourceRef.setHighlight     = null;
      navSourceRef.setEraserSegs    = null;
      navSourceRef.setPathLayersVis = null;
      navSourceRef.applyVisibility  = null;
      navSourceRef.onInit           = null;
    };
    
    const init = () => {
      if (cancelled) return;
      if (initialised.current) return;
      if (dashRafId) { cancelAnimationFrame(dashRafId); dashRafId = null; }

      map.addSource(NAV_SOURCES.lines, {
        type: 'geojson', data: emptyCol(), promoteId: 'id',
      });
      map.addSource(NAV_SOURCES.nodes, {
        type: 'geojson', data: emptyCol(), promoteId: 'id',
      });
      map.addSource(NAV_SOURCES.preview, {
        type: 'geojson', data: emptyCol(),
      });
      map.addSource(NAV_SOURCES.eraserSegs, {
        type: 'geojson', data: emptyCol(),
      });
      
      map.addLayer({
        id: NAV_LAYERS.mainLine, type: 'line', source: NAV_SOURCES.lines,
        filter: ['all', ['==', ['get', 'pathType'], 'main'], ['!', ['get', 'selected']]],
        layout: { 'line-cap': 'round', 'line-join': 'round', 'visibility': 'none' },
        paint:  { 'line-color': '#1a73e8', 'line-width': 3, 'line-opacity': 0.9 },
      });
      
      map.addLayer({
        id: NAV_LAYERS.subLine, type: 'line', source: NAV_SOURCES.lines,
        filter: ['all', ['==', ['get', 'pathType'], 'sub'], ['!', ['get', 'selected']]],
        layout: { 'line-cap': 'round', 'line-join': 'round', 'visibility': 'none' },
        paint:  { 'line-color': '#2e7d32', 'line-width': 2.5, 'line-opacity': 0.9 },
      });
      
      map.addLayer({
        id: NAV_LAYERS.selectedLine, type: 'line', source: NAV_SOURCES.lines,
        filter: ['==', ['get', 'selected'], true],
        layout: { 'line-cap': 'round', 'line-join': 'round', 'visibility': 'none' },
        paint:  { 'line-color': '#f57c00', 'line-width': 4, 'line-opacity': 1 },
      });
      
      map.addLayer({
        id: NAV_LAYERS.hitLine, type: 'line', source: NAV_SOURCES.lines,
        layout: { 'line-cap': 'round', 'visibility': 'none' },
        paint:  { 'line-color': 'rgba(0,0,0,0)', 'line-width': 16, 'line-opacity': 0 },
      });
      
      map.addLayer({
        id: NAV_LAYERS.nodes, type: 'circle', source: NAV_SOURCES.nodes,
        filter: ['all', ['!', ['get', 'isPin']], ['!', ['get', 'isSnap']]],
        layout: { 'visibility': 'none' },
        paint: {
          'circle-radius': ['case', ['get', 'selected'], 8, 6],
          'circle-color': [
            'case',
            ['get', 'selected'],                         '#f57c00',
            ['==', ['get', 'pathType'], 'main'],         '#1a73e8',
            '#2e7d32',
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
      
      map.addLayer({
        id: NAV_LAYERS.nodesSnap, type: 'circle', source: NAV_SOURCES.nodes,
        filter: ['get', 'isSnap'],
        layout: { 'visibility': 'none' },
        paint: {
          'circle-radius':       7,
          'circle-color':        '#FFD600',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
      
      map.addLayer({
        id: NAV_LAYERS.nodesPin, type: 'circle', source: NAV_SOURCES.nodes,
        filter: ['get', 'isPin'],
        layout: { 'visibility': 'none' },
        paint: {
          'circle-radius':       ['case', ['get', 'selected'], 9, 6],
          'circle-color':        ['case', ['get', 'selected'], '#f57c00', '#7048e8'],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
          'circle-opacity':      ['case', ['get', 'selected'], 1, 0.7],
        },
      });
      
      map.addLayer({
        id: NAV_LAYERS.eraserSegs, type: 'line', source: NAV_SOURCES.eraserSegs,
        layout: { 'line-cap': 'round', 'line-join': 'round', 'visibility': 'none' },
        paint: {
          'line-color':   '#f57c00',
          'line-width':   5,
          'line-opacity': 0.9,
        },
      });
      
      map.addLayer({
        id: NAV_LAYERS.previewLine, type: 'line', source: NAV_SOURCES.preview,
        filter: ['==', ['geometry-type'], 'LineString'],
        layout: { 'line-cap': 'round', 'line-join': 'round', 'visibility': 'none' },
        paint: {
          'line-color': [
            'case', ['==', ['get', 'pathType'], 'main'], '#1a73e8', '#2e7d32',
          ],
          'line-width':     2,
          'line-dasharray': [4, 3],
          'line-opacity':   0.8,
        },
      });
      
      map.addLayer({
        id: NAV_LAYERS.previewDot, type: 'circle', source: NAV_SOURCES.preview,
        filter: ['==', ['geometry-type'], 'Point'],
        layout: { 'visibility': 'none' },
        paint: {
          'circle-radius':       5,
          'circle-color': [
            'case', ['==', ['get', 'pathType'], 'main'], '#1a73e8', '#2e7d32',
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
      
      navSourceRef.setLines = (paths, selectedPathId) => {
        map.getSource(NAV_SOURCES.lines)
        ?.setData(pathsToLinesGeoJSON(paths, selectedPathId));
      };
      navSourceRef.setNodes = (paths, selectedPathId, selectedPointId, eraserPointIds = null) => {
        map.getSource(NAV_SOURCES.nodes)
        ?.setData(nodesToGeoJSON(paths, selectedPathId, selectedPointId, eraserPointIds));
      };
      navSourceRef.setEraserSegs = (paths, eraserSelectedPoints) => {
        map.getSource(NAV_SOURCES.eraserSegs)
        ?.setData(eraserSegmentsToGeoJSON(paths, eraserSelectedPoints));
      };
      navSourceRef.setPreview = (inProgress, mousePos) => {
        map.getSource(NAV_SOURCES.preview)
        ?.setData(previewToGeoJSON(inProgress, mousePos));
      };
      
      const ARROW_SZ = 22;
      const arrowCanvas = document.createElement('canvas');
      arrowCanvas.width  = ARROW_SZ;
      arrowCanvas.height = ARROW_SZ;
      const arrowCtx = arrowCanvas.getContext('2d');
      arrowCtx.fillStyle = 'rgba(255,255,255,0.95)';
      arrowCtx.beginPath();
      arrowCtx.moveTo(ARROW_SZ,          ARROW_SZ / 2); 
      arrowCtx.lineTo(ARROW_SZ * 0.35,   0);           
      arrowCtx.lineTo(ARROW_SZ * 0.55,   ARROW_SZ / 2); 
      arrowCtx.lineTo(ARROW_SZ * 0.35,   ARROW_SZ);     
      arrowCtx.closePath();
      arrowCtx.fill();
      if (!map.hasImage('nav-direction-arrow')) {
        map.addImage(
          'nav-direction-arrow',
          arrowCtx.getImageData(0, 0, ARROW_SZ, ARROW_SZ),
        );
      }
      
      map.addSource(NAV_SOURCES.highlight, {
        type: 'geojson', data: emptyCol(),
      });
      
      map.addLayer({
        id: NAV_LAYERS.highlightLine, type: 'line', source: NAV_SOURCES.highlight,
        layout: { 'line-cap': 'round', 'line-join': 'round', 'visibility': 'none' },
        paint: {
          'line-color':   projectData.navigation_color ?? '#35e558', 
          'line-width':   7,
          'line-opacity': 0.95,
        },
      });
      
      map.addLayer({
        id: NAV_LAYERS.highlightDash, type: 'line', source: NAV_SOURCES.highlight,
        layout: { 'line-cap': 'butt', 'line-join': 'round', 'visibility': 'none' },
        paint: {
          'line-color':     '#ffffff',
          'line-width':     projectData.navigation_thick ?? 3,
          'line-opacity':   0.85,
          'line-dasharray': [0, 4, 3],
        },
      });
      
      map.addLayer({
        id: NAV_LAYERS.highlightArrow, type: 'symbol', source: NAV_SOURCES.highlight,
        layout: {
          'visibility':              'none',
          'symbol-placement':        'line',
          'symbol-spacing':          80,
          'icon-image':              'nav-direction-arrow',
          'icon-size':               0.9,
          'icon-rotation-alignment': 'map',
          'icon-pitch-alignment':    'viewport',
          'icon-allow-overlap':      true,
          'icon-ignore-placement':   true,
        },
      });
      
      navSourceRef.setHighlight = (positions) => { 
        navSourceRef._latestHighlight = positions ?? null;
        const data = positions && positions.length >= 2
        ? {
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: positions },
            properties: {},
          }],
        }
        : emptyCol();
        map.getSource(NAV_SOURCES.highlight)?.setData(data);
      };
      
      if (navSourceRef._latestHighlight) {
        navSourceRef.setHighlight(navSourceRef._latestHighlight);
      }
      
      navSourceRef.applyVisibility = (v) => {
        Object.values(NAV_LAYERS).forEach((l) => {
          try { if (map.getLayer(l)) map.setLayoutProperty(l, 'visibility', v); } catch {}
        });
      };
      
      navSourceRef.applyVisibility(navSourceRef._desiredVisibility);
      navSourceRef.onInit?.();

      const dashFrames = [
        [0, 4, 3], [0.5, 4, 2.5], [1, 4, 2],   [1.5, 4, 1.5],
        [2, 4, 1], [2.5, 4, 0.5], [3, 4, 0],
        [0, 0.5, 3, 3.5], [0, 1, 3, 3], [0, 1.5, 3, 2.5],
        [0, 2, 3, 2],     [0, 2.5, 3, 1.5], [0, 3, 3, 1], [0, 3.5, 3, 0.5],
      ];
      let dashStep   = 0;
      let lastDashTs = 0;
      const DASH_MS  = 50; 
      
      const animateDash = (ts) => {
        dashRafId = requestAnimationFrame(animateDash);
        if (ts - lastDashTs < DASH_MS) return;
        lastDashTs = ts;
        try {
          map.setPaintProperty(
            NAV_LAYERS.highlightDash, 'line-dasharray', dashFrames[dashStep],
          );
        } catch { }
        dashStep = (dashStep + 1) % dashFrames.length;
      };
      dashRafId = requestAnimationFrame(animateDash);
      
      initialised.current = true;
    };
    
    const handleStyleData = () => {
      if (cancelled) return;
      if (!map.isStyleLoaded()) return;
      if (map.getSource(NAV_SOURCES.lines)) return;
      initialised.current = false;
      navSourceRef.setLines         = null;
      navSourceRef.setNodes         = null;
      navSourceRef.setPreview       = null;
      navSourceRef.setHighlight     = null;
      navSourceRef.setEraserSegs    = null;
      navSourceRef.setPathLayersVis = null;
      navSourceRef.applyVisibility  = null;
      init();
    };
    
    if (map.isStyleLoaded()) init();
    else map.once('load', init);
    map.on('styledata', handleStyleData);

    return () => {
      cancelled = true;
      map.off('load', init);
      map.off('styledata', handleStyleData);
      initialised.current = false;
      teardown();
    };
  }, [map]);
  
  // useEffect(() => {
  //   if (!map || !projectData) return;
    
  //   if (!map.getLayer(NAV_LAYERS.highlightLine)) return;
    
  //   try {
  //     map.setPaintProperty(
  //       NAV_LAYERS.highlightLine,
  //       'line-color',
  //       projectData.navigation_color ?? '#e53535'
  //     );
      
  //     map.setPaintProperty(
  //       NAV_LAYERS.highlightLine,
  //       'line-width',
  //       projectData.navigation_thick ?? 7
  //     );
  //   } catch (e) {
  //   }
  // }, [map, projectData?.navigation_color, projectData?.navigation_thick]);
  
  useEffect(() => {
    if (!map || !projectData) return;
    
    const applyHighlightStyle = () => {
      if (!map.getLayer(NAV_LAYERS.highlightLine)) return;
      try {
        map.setPaintProperty(
          NAV_LAYERS.highlightLine,
          'line-color',
          projectData.navigation_color ?? '#e53535'
        );
        map.setPaintProperty(
          NAV_LAYERS.highlightLine,
          'line-width',
          projectData.navigation_thick ?? 7
        );
      } catch (e) {
        console.log('Highlight style update failed', e);
      }
    };
    
    if (map.isStyleLoaded()) {
      applyHighlightStyle();
    } else {
      map.once('style.load', applyHighlightStyle);
      return () => map.off('style.load', applyHighlightStyle);
    }
  }, [map, projectData?.navigation_color, projectData?.navigation_thick]);
  
  return null;
}

export function NavSync() {
  const dispatch              = useDispatch();
  const allPaths              = useSelector((s) => s.navigation.paths);
  const selectedPathId        = useSelector((s) => s.navigation.selectedPathId);
  const selectedPointId       = useSelector((s) => s.navigation.selectedPointId);
  const eraserSelectedPoints  = useSelector((s) => s.navigation.eraserSelectedPoints);
  const inProgress            = useSelector((s) => s.navigation.inProgress);
  const shortestPath          = useSelector((s) => s.navigation.shortestPath);
  const currentFloor          = useSelector((s) => s.api.currentFloor);
  const isNavPage              = !!useMatch('/project/:id/navigation');

  const floorId = currentFloor?.enc_id ?? null;
  
  const floorPaths = useMemo(
    () => allPaths.filter((p) => p.floorId != null && floorId != null && String(p.floorId) === String(floorId)),
    [allPaths, floorId],
  );
  
  const eraserPointIds = useMemo(
    () => eraserSelectedPoints.length > 0
    ? new Set(eraserSelectedPoints.map((p) => p.pointId))
    : null,
    [eraserSelectedPoints],
  );
  
  const [initCount, setInitCount] = useState(0);
  useEffect(() => {
    navSourceRef.onInit = () => setInitCount((c) => c + 1);
    return () => { navSourceRef.onInit = null; };
  }, []);
  
  useEffect(() => {
    navSourceRef.setLines?.(floorPaths, selectedPathId);
    navSourceRef.setNodes?.(floorPaths, selectedPathId, selectedPointId, eraserPointIds);
  }, [floorPaths, selectedPathId, selectedPointId, eraserPointIds, initCount, isNavPage]);
 
  useEffect(() => {
    if (!isNavPage) return;
    let ticks = 0;
    const id = setInterval(() => {
      ticks += 1;
      navSourceRef.setLines?.(floorPaths, selectedPathId);
      navSourceRef.setNodes?.(floorPaths, selectedPathId, selectedPointId, eraserPointIds);
      if (ticks >= 20) clearInterval(id);
    }, 150);
    return () => clearInterval(id);
  }, [isNavPage, floorPaths, selectedPathId, selectedPointId, eraserPointIds]);
  
  useEffect(() => {
    navSourceRef.setEraserSegs?.(floorPaths, eraserSelectedPoints);
  }, [floorPaths, eraserSelectedPoints]);
  
  useEffect(() => {
    dispatch(clearNavSelection());
  }, [floorId, dispatch]);
  
  useEffect(() => {
    if (!inProgress) {
      navSourceRef.setPreview?.(null, null);
    }
  }, [inProgress]);
  
  useEffect(() => {
    if (!shortestPath) {
      navSourceRef.setHighlight?.(null);
      return;
    }
    
    const positions = shortestPath.segments
    ? (shortestPath.segments.find((s) => floorId != null && String(s.floorId) === String(floorId))?.positions ?? null)
    : (shortestPath.positions ?? null);
    navSourceRef.setHighlight?.(positions);
  }, [shortestPath, floorId]);
  
  return null;
}

export function NavVisibility() {
  const isNavPage = !!useMatch('/project/:id/navigation');

  useEffect(() => {
    const visibility = isNavPage ? 'visible' : 'none';
    navSourceRef._desiredVisibility = visibility;
    navSourceRef.applyVisibility?.(visibility);
 
    if (!isNavPage) return;
    let ticks = 0;
    const id = setInterval(() => {
      ticks += 1;
      navSourceRef.applyVisibility?.(visibility);
      if (ticks >= 20) clearInterval(id);
    }, 150);
    return () => clearInterval(id);
  }, [isNavPage]);

  return null;
}

const LS_KEY      = (projectId) => `nav-paths-${projectId}`;
const MAX_RETRIES = 1;
const RETRY_BASE_MS = 2000;

export function NavAutoSave() {
  const dispatch     = useDispatch();
  const store        = useStore();
  const paths        = useSelector((s) => s.navigation.paths);
  const currentFloor = useSelector((s) => s.api.currentFloor);
  const reloadToken  = useSelector((s) => s.navigation.reloadToken);
  const { id }       = useParams();
  const decodedId    = decode(id);
  const { handleEnableDisable } = useProjectHeader();
  
  const floorId = currentFloor?.enc_id ?? null;
  
  const floorPaths = useMemo(
    () => paths.filter((p) => p.floorId != null && floorId != null && String(p.floorId) === String(floorId)),
    [paths, floorId],
  );
  
  const hasLoaded   = useRef(false);
  const skipSaveRef = useRef(false);
  const prevFloorId = useRef(floorId);
  const saveTimer   = useRef(null);
  const statusTimer = useRef(null);
  const saveVersion = useRef(0);
  
  const markSaved = () => {
    dispatch(setSaveStatus('saved'));
    if (statusTimer.current) clearTimeout(statusTimer.current);
    statusTimer.current = setTimeout(() => dispatch(setSaveStatus('idle')), 3000);
  };
  
  const attemptSave = (projectId, floorplanId, snapshot, version, attempt = 0) => {
    if (version !== saveVersion.current) return;
    
    saveNavigationPaths(projectId, floorplanId, snapshot)
    .then((result) => {
      if (version !== saveVersion.current) return;
      if (result?.type === 1) {
        markSaved();
        handleEnableDisable();
      } else {
        throw new Error(result?.errormessage || 'Save failed');
      }
    })
    .catch(() => {
      if (version !== saveVersion.current) return;
      if (attempt < MAX_RETRIES - 1) {
        setTimeout(
          () => attemptSave(projectId, floorplanId, snapshot, version, attempt + 1),
          RETRY_BASE_MS * Math.pow(2, attempt),
        );
      } else {
        dispatch(setSaveStatus('failed'));
      }
    });
  };
   
  useEffect(() => {
    if (!decodedId) return;

    hasLoaded.current = false;
    dispatch(clearAllNavPaths());
    dispatch(setSaveStatus('idle'));

    const load = async () => {
      try {
        let savedPaths = await loadNavPathsFromProject(decodedId);

        if (!savedPaths?.length) {
          try {
            // const raw = localStorage.getItem(LS_KEY(decodedId));
            // if (raw) savedPaths = JSON.parse(raw) ?? [];
          } catch {}
        }
         
        const alreadyEdited = store.getState().navigation.paths.length > 0;
        if (savedPaths?.length > 0 && !alreadyEdited) {
          skipSaveRef.current = true;
          dispatch(setAllPaths(savedPaths));
        }
      } catch {
        try {
          // const raw = localStorage.getItem(LS_KEY(decodedId));
          // if (raw) {
          //   const localPaths = JSON.parse(raw) ?? [];
          //   if (localPaths.length > 0) {
          //     skipSaveRef.current = true;
          //     dispatch(setAllPaths(localPaths));
          //   }
          // }
        } catch {}
      } finally {
        hasLoaded.current = true;
      }
    };
    
    load();

    return () => {
      if (saveTimer.current)  clearTimeout(saveTimer.current);
      if (statusTimer.current) clearTimeout(statusTimer.current);
    };
  }, [decodedId, dispatch, reloadToken]);
  
  useEffect(() => {
    if (prevFloorId.current !== floorId) {
      prevFloorId.current = floorId;
      skipSaveRef.current = true;
    }
  }, [floorId]);
  
  useEffect(() => {
    if (!hasLoaded.current) return;
    
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }
    
    if (!floorId) return;
    
    if (saveTimer.current) clearTimeout(saveTimer.current);
    const snapshot = floorPaths;
    
    saveTimer.current = setTimeout(() => {
      saveVersion.current += 1;
      const version = saveVersion.current;
      
      dispatch(setSaveStatus('saving'));
      attemptSave(decodedId, floorId, snapshot, version);
    }, 1000);
  }, [floorPaths, decodedId, floorId]);
  
  return null;
}
